import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { ErrorBanner, Notice } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import {
  useCreateWorkspace,
  useDeleteWorkspace,
  useForgetWorkspace,
  useMembers,
  useTransferOwnership,
  useUpdateWorkspace,
} from "../../api/admin";
import { useMe } from "../../api/session";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { ROLE_LABEL } from "../../lib/permissions";
import { card } from "./SettingsLayout";

export function WorkspaceSettingsPage() {
  const canEdit = useCan("workspace:settings");
  const canDelete = useCan("workspace:delete");
  return (
    <>
      {canEdit ? <EditWorkspaceForm /> : <WorkspaceDetails />}
      {canDelete && <TransferOwnership />}
      {canDelete && <DeleteWorkspace />}
      <NewWorkspaceForm />
    </>
  );
}

function WorkspaceDetails() {
  const ws = useWorkspace();
  return (
    <section className={card} aria-labelledby="ws-details">
      <h2 id="ws-details" className="mb-3 font-semibold">
        Workspace
      </h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted">Nome</dt>
        <dd>{ws.name}</dd>
        <dt className="text-muted">Moeda</dt>
        <dd>{ws.defaultCurrency}</dd>
        <dt className="text-muted">Fuso horário</dt>
        <dd>{ws.timezone}</dd>
        <dt className="text-muted">Seu papel</dt>
        <dd>{ROLE_LABEL[ws.role]}</dd>
      </dl>
    </section>
  );
}

function EditWorkspaceForm() {
  const ws = useWorkspace();
  const update = useUpdateWorkspace(ws.id);
  const [name, setName] = useState(ws.name);
  const [currency, setCurrency] = useState(ws.defaultCurrency);
  const [timezone, setTimezone] = useState(ws.timezone);
  const [saved, setSaved] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    await update.mutateAsync({
      name: name.trim(),
      defaultCurrency: currency.trim().toUpperCase(),
      timezone: timezone.trim(),
    });
    setSaved(true);
  }

  return (
    <form onSubmit={(e) => void submit(e).catch(() => undefined)} className={`${card} flex flex-col gap-3`}>
      <h2 className="font-semibold">Workspace</h2>
      <TextInput
        label="Nome do workspace"
        required
        maxLength={80}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextInput
        label="Moeda padrão"
        hint="Código ISO 4217, ex.: BRL"
        required
        pattern="[A-Za-z]{3}"
        maxLength={3}
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
      />
      <TextInput
        label="Fuso horário"
        hint="Ex.: America/Sao_Paulo"
        required
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
      />
      <ErrorBanner error={update.error} />
      {saved && <Notice>Alterações salvas.</Notice>}
      <Button type="submit" disabled={update.isPending}>
        Salvar alterações
      </Button>
    </form>
  );
}

function TransferOwnership() {
  const ws = useWorkspace();
  const members = useMembers(ws.id);
  const transfer = useTransferOwnership(ws.id);
  const [userId, setUserId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const me = useMe();
  const others = (members.data ?? []).filter((m) => m.userId !== me.data?.id && m.role !== "owner");
  if (others.length === 0) return null;
  const target = others.find((m) => m.userId === userId);

  return (
    <section className={`${card} flex flex-col gap-3`} aria-labelledby="transfer-title">
      <h2 id="transfer-title" className="font-semibold">
        Transferir propriedade
      </h2>
      <p className="text-sm text-muted">A pessoa escolhida vira proprietária; você passa a administrador.</p>
      <Select
        label="Novo proprietário"
        value={userId}
        onChange={(e) => {
          setUserId(e.target.value);
          setConfirming(false);
        }}
      >
        <option value="">Escolha um membro</option>
        {others.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.email}
          </option>
        ))}
      </Select>
      <ErrorBanner error={transfer.error} />
      {confirming && target ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Transferir para {target.email}?</span>
          <Button
            variant="danger"
            disabled={transfer.isPending}
            onClick={() => transfer.mutate(target.userId)}
          >
            Confirmar transferência
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancelar
          </Button>
        </div>
      ) : (
        <Button variant="secondary" disabled={!target} onClick={() => setConfirming(true)}>
          Transferir propriedade
        </Button>
      )}
    </section>
  );
}

function DeleteWorkspace() {
  const ws = useWorkspace();
  const del = useDeleteWorkspace(ws.id);
  const forget = useForgetWorkspace();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  async function confirm() {
    await del.mutateAsync();
    forget(ws.id);
    navigate("/", { replace: true });
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-card border border-expense bg-surface p-5 shadow-card lg:p-6 [&_h2]:m-0 [&_h2]:text-[17px]"
      aria-labelledby="delete-title"
    >
      <h2 id="delete-title" className="font-semibold text-expense">
        Excluir workspace
      </h2>
      <p className="text-sm text-muted">
        Apaga contas, lançamentos e histórico de “{ws.name}”. Não dá para desfazer.
      </p>
      <ErrorBanner error={del.error} />
      {confirming ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="danger"
            disabled={del.isPending}
            onClick={() => void confirm().catch(() => undefined)}
          >
            Confirmar exclusão
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancelar
          </Button>
        </div>
      ) : (
        <Button variant="danger" onClick={() => setConfirming(true)}>
          Excluir workspace
        </Button>
      )}
    </section>
  );
}

export function NewWorkspaceForm() {
  const create = useCreateWorkspace();
  const navigate = useNavigate();
  const [name, setName] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ws = await create.mutateAsync({ name: name.trim() });
    setName("");
    navigate(`/w/${ws.id}`);
  }

  return (
    <form
      onSubmit={(e) => void submit(e).catch(() => undefined)}
      className={`${card} flex flex-col gap-3 text-left`}
    >
      <h2 className="font-semibold">Novo workspace</h2>
      <TextInput
        label="Nome do novo workspace"
        required
        maxLength={80}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <ErrorBanner error={create.error} />
      <Button type="submit" disabled={create.isPending}>
        Criar workspace
      </Button>
    </form>
  );
}
